import __CapabilityFiltersScss from './CapabilityFilters.scss';
import __CapabilityFiltersOutlookdesktopScss from './CapabilityFilters.outlookdesktop.scss';
import __CapabilityFiltersMacoutlookScss from './CapabilityFilters.macoutlook.scss';
import { getAdjustedFloorString } from './utils/getAdjustedStringUtils';
import { updateFloorValue } from '../../mutators/capabilityFilterMutators';
import { getRoomPanelViewState } from '../../selectors/getRoomPanelViewState';
import { useClientStyles } from 'owa-react-hooks/lib/useClientStyles';
import { observer } from 'mobx-react-lite';
import { SpinButton, ISpinButtonStyleProps } from '@fluentui/react/lib/SpinButton';
import loc from 'owa-localize';
import React from 'react';
import {
    emptySpinButtonLabel,
    floorDecreaseAriaLabel,
    floorIncreaseAriaLabel,
    floorFilterLabel,
} from './CapabilityFilters.locstring.json';
import { useModeStyles } from 'owa-react-hooks/lib/useModeStyles';

export interface FloorFilterProps {
    minFloor: number;
    maxFloor: number;
    viewStateId: string | null;
    onChange?: () => void;
}

const styles = {
    base: __CapabilityFiltersScss,
    desktop: __CapabilityFiltersOutlookdesktopScss,
    mac: {
        ...__CapabilityFiltersMacoutlookScss,
        spinButtonWrapperFocused: {
            border: '4px solid !important',
        },
    },
};

export default observer(function FloorFilter(props: FloorFilterProps) {
    const { getClass, getStyle } = useClientStyles(styles);
    const { getModeAwareClass } = useModeStyles(getClass);
    const getDefaultFloorValue = () => {
        const { selectedFloor } = getRoomPanelViewState(props.viewStateId).roomFilterState;
        return getAdjustedFloorString(selectedFloor, props.maxFloor, props.minFloor);
    };

    const onFloorIncrement = (floorValue: string) => {
        let newFloor;
        const { maxFloor, minFloor } = props;
        // "Any" is considered as minFloor - 1 so that users can change
        // spin button value back to "any" using increase/decrease button
        if (!floorValue || floorValue === loc(emptySpinButtonLabel)) {
            newFloor = minFloor;
        } else {
            newFloor = Number(floorValue) + 1;
        }
        const updatedFloor = getAdjustedFloorString(String(newFloor), maxFloor, minFloor);
        onChange(updatedFloor);
        return getDefaultFloorValue();
    };

    const onFloorDecrement = (floorValue: string) => {
        const newFloor = Number(floorValue) - 1;
        const updatedFloor = getAdjustedFloorString(
            String(newFloor),
            props.maxFloor,
            props.minFloor
        );
        onChange(updatedFloor);
        return getDefaultFloorValue();
    };

    const onFloorValidate = (floor: string) => {
        const adjustedFloor = getAdjustedFloorString(floor, props.maxFloor, props.minFloor);
        onChange(adjustedFloor);
        return getDefaultFloorValue();
    };

    const onChange = (floor: string) => {
        updateFloorValue(floor, props.viewStateId);
        props.onChange?.();
    };
    const getSpinButtonStyles = ({ isFocused }: ISpinButtonStyleProps) => ({
        spinButtonWrapper: [
            getModeAwareClass('spinButtonWrapper'),
            isFocused && getStyle('spinButtonWrapperFocused'),
        ],
        input: getModeAwareClass('spinButtonInput'),
    });

    return (
        <SpinButton
            value={getDefaultFloorValue()}
            // if minFloor is Infinity, maxFloor should be -Infinity at the same time, which means no floor meta data is set in room list
            disabled={!isFinite(props.minFloor)}
            styles={getSpinButtonStyles}
            onValidate={onFloorValidate}
            onIncrement={onFloorIncrement}
            onDecrement={onFloorDecrement}
            min={isFinite(props.minFloor) ? props.minFloor : 0}
            max={isFinite(props.maxFloor) ? props.maxFloor : 0}
            incrementButtonAriaLabel={loc(floorIncreaseAriaLabel)}
            decrementButtonAriaLabel={loc(floorDecreaseAriaLabel)}
            inputProps={{ 'aria-label': loc(floorFilterLabel) }}
        />
    );
});
